/**
 * Spec 048: «Радар» — the base of findings from the internet. Staff report into it through
 * their runs; people keep it here: rings, trust, merging duplicates, rejecting and deleting.
 */

import { api } from "@/api/client";

export type RadarKind = "repo" | "service" | "skill" | "mcp" | "package" | "app" | "model" | "method" | "visual";
export type RadarState = "review" | "listed" | "merged" | "rejected";
export type RadarRing = "adopt" | "trial" | "assess" | "hold";
export type RadarTrust = "rumor" | "confirmed" | "tried";
export type RadarCaseOk = "yes" | "no" | "ask";
/** How far a staff member's card reaches into «Радар»; each level includes the one before. */
export type RadarRight = "none" | "read" | "report" | "edit";

export interface RadarWho {
  kind: "person" | "agent";
  id: string;
  name: string;
}

export interface RadarItemOut {
  id: string;
  kind: RadarKind;
  key: string | null;
  title: string;
  summary_md: string | null;
  tags: string[];
  source_url: string | null;
  state: RadarState;
  ring: RadarRing | null;
  trust: RadarTrust;
  trust_note: string | null;
  trust_at: string | null;
  reason_md: string | null;
  latest_version: string | null;
  released_at: string | null;
  checked_at: string | null;
  recheck_by: string | null;
  due: boolean;
  fading: boolean;
  mentions: number;
  last_signal_at: string | null;
  replaced_by: string | null;
  merged_into: string | null;
  preview_url: string | null;
  code_url: string | null;
  stack: string[];
  block: string | null;
  license: string | null;
  case_ok: RadarCaseOk | null;
  created_by: RadarWho;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface RadarMentionOut {
  id: string;
  channel: string;
  url: string;
  author: string | null;
  seen_at: string;
  metrics: Record<string, number>;
  note: string | null;
  by: RadarWho | null;
}

export interface RadarItemDetail extends RadarItemOut {
  recent_mentions: RadarMentionOut[];
  channels: string[];
  versions: { version: string; released_at: string | null; url: string | null; created_at: string }[];
  uses: {
    id: string;
    task_id: string | null;
    case_id: string | null;
    order_id: string | null;
    outcome: "success" | "partial" | "failed";
    note: string | null;
    by: RadarWho | null;
    created_at: string;
  }[];
  history: { at: string; type: string; by: RadarWho | null; payload: Record<string, unknown> }[];
}

export interface RadarTrendOut {
  item: RadarItemOut;
  mentions: number;
  channels: number;
  growth: Record<string, number>;
}

export interface RadarUpdateIn {
  version: number;
  ring?: RadarRing;
  trust?: "rumor" | "confirmed";
  trust_note?: string;
  checked?: boolean;
  reject?: boolean;
  restore?: boolean;
  reason_md?: string;
  title?: string;
  summary_md?: string | null;
}

export interface RadarQuery {
  q?: string;
  kind?: RadarKind;
  state?: "review" | "listed" | "rejected" | "all";
  ring?: RadarRing | "review";
  trust?: RadarTrust;
  due?: boolean;
  fading?: boolean;
  case_ok?: RadarCaseOk;
  limit?: number;
}

function query(params: RadarQuery): string {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    out.set(key, String(value));
  }
  const text = out.toString();
  return text ? `?${text}` : "";
}

export const Radar = {
  list: (params: RadarQuery = {}) => api<RadarItemOut[]>(`/api/v1/departments/radar/items${query(params)}`),
  get: (id: string) => api<RadarItemDetail>(`/api/v1/departments/radar/items/${id}`),
  /** Send the version you read: 409 means someone changed it meanwhile. */
  update: (id: string, body: RadarUpdateIn) => api<RadarItemOut>(`/api/v1/departments/radar/items/${id}`, { method: "PATCH", body }),
  merge: (id: string, version: number, intoId: string) =>
    api<RadarItemOut>(`/api/v1/departments/radar/items/${id}/merge`, { method: "POST", body: { version, into_id: intoId } }),
  /** Deleting is a person's own action: no agent may. */
  remove: (id: string) => api<void>(`/api/v1/departments/radar/items/${id}`, { method: "DELETE" }),
  trends: (days = 7) => api<RadarTrendOut[]>(`/api/v1/departments/radar/trends?days=${days}`),
};
